<template>
    <div v-show="firstName && isMobileEligible && isMobileInterested"
        class="text-navy-dark bg-white rounded-md shadow print:hidden p-6 mb-4">
        <h3 class="text-xl mb-4">
            {{ heading }}
        </h3>
        <div v-if="!accountCreation" class="text-sm mt-3 font-normal">
            {{ modalCopy }}
        </div>
        <a v-if="!accountCreation" class="buyflow-button mt-5" :href="modalLink" target="_blank"
            @click="dismissModal()">
            {{ modalButton }}
        </a>
        <form v-if="accountCreation" class="w-full tablet:max-w-30" autocomplete="off">
            <div class="mt-8">
                <h4 class="text-xl mb-4">Create Username</h4>
                <p class="mb-4">
                    This username will be used to sign in and manage your
                    Breezeline account and mobile apps. You can also opt to create a breezeline.net email account.
                </p>
                <div class="mt-1 block w-full rounded-sm border-gray-400">
                    <label for="username" class="hide-pw-managers font-bold">
                        Username
                    </label>
                    <input v-model="formFields.username"
                        class="smMobile:w-full tablet:w-full block flex-grow rounded-sm border-gray-400 placeholder:text-slate-200 mb-2"
                        :class="{
                            'border-salmon':
                                $v.formFields.username.$error ||
                                errors.username ||
                                errors.jwe,
                        }" name="username" placeholder="Enter username" type="text" autocomplete="off" />
                    <input v-model="formFields.bzlEmail" type="checkbox" id="bzlEmail" name="bzlEmail"> <span
                        class="font-medium">Create a
                        breezeline.net email address (optional) </span>
                </div>
                <span v-if="errors.jwe" class="text-salmon text-sm">
                    <IconCircleExclamation class="inline mr-1" /> We encountered
                    an issue creating your account. Please try again.
                </span>
                <span v-else-if="$v.formFields.username.$error" class="text-salmon text-sm">
                    <IconCircleExclamation class="inline mr-1" />  {{ usernameErrorMsg }}
                </span>
                <span v-else-if="errors.username" class="text-salmon text-sm">
                    <IconCircleExclamation class="inline mr-1" />
                    <span v-html="errors.username" />
                </span>
            </div>
            <div class="mt-8">
                <h4 class="text-xl mb-4">Create Your Password</h4>
                <p class="mb-4 pr-4">
                    Passwords must be 8 to 24 characters, include at least: 1
                    letter (case sensitive), 1 number, and 1 of these special
                    characters: @ ! $ % + / = ~
                </p>
                <div class="relative">
                    <label for="password" class="hide-pw-managers font-bold">
                        New Password
                        <input v-model="formFields.password" class="mt-1 block w-full rounded-sm border-gray-400"
                            :class="{
                                'border-salmon':
                                    $v.formFields.password.$error ||
                                    errors.password,
                            }" name="password" autocomplete="new-passwords"
                            :type="passwordVisible ? 'text' : 'password'" />
                        <i v-if="passwordsMatch" class="absolute right-8 bottom-0.5">
                            <IconCircleCheck class="p-3 h-10 w-10 text-green-500" />
                        </i>
                        <button type="button" @click.prevent="passwordVisible = !passwordVisible"
                            class="absolute right-1 bottom-0.5">
                            <IconEyeCancel v-if="passwordVisible" class="p-2 h-10 w-10 text-blue-light" />
                            <IconEye v-else class="p-2 h-10 w-10 text-blue-light" />
                        </button>
                    </label>
                </div>
                <div>
                    <div class="grid grid-cols-4 h-5 gap-1">
                        <span class="col-span-1 rounded-sm bg-gray-200 my-1.5" :class="meter[0]" />
                        <span class="col-span-1 rounded-sm bg-gray-200 my-1.5" :class="meter[1]" />
                        <span class="col-span-1 rounded-sm bg-gray-200 my-1.5" :class="meter[2]" />
                        <span class="col-span-1 rounded-sm bg-gray-200 my-1.5" :class="meter[3]" />
                    </div>
                    <span v-if="$v.formFields.password.$error || errors.password"
                        class="float-left text-salmon text-sm">
                        <IconCircleExclamation class="inline mr-1" />
                        <span v-html="errors.password" />
                    </span>
                    <span v-if="meter[4]" class="float-right font-bold">
                        {{ meter[4] }}
                    </span>
                </div>
                <div class="mt-6 relative">
                    <label for="confirm-password" class="hide-pw-managers font-bold">
                        Confirm Password
                        <input v-model="formFields.confirmPassword" class="mt-1 block w-full rounded-sm border-gray-400"
                            :class="{
                                'border-salmon':
                                    $v.formFields.confirmPassword.$error,
                            }" name="confirm-password" autocomplete="new-passwords"
                            :type="confirmPasswordVisible ? 'text' : 'password'" />
                        <i v-if="passwordsMatch" class="absolute right-8 bottom-0.5">
                            <IconCircleCheck class="p-3 h-10 w-10 text-green-500" />
                        </i>
                        <button type="button" @click.prevent="
                            confirmPasswordVisible = !confirmPasswordVisible
                            " class="absolute right-1 bottom-0.5">
                            <IconEyeCancel v-if="confirmPasswordVisible" class="p-2 h-10 w-10 text-blue-light" />
                            <IconEye v-else class="p-2 h-10 w-10 text-blue-light" />
                        </button>
                    </label>
                </div>
                <span v-if="$v.formFields.confirmPassword.$error" class="text-salmon text-sm">
                    <IconCircleExclamation class="inline mr-1" /> Password does
                    not match
                </span>
            </div>
            <button type="button"
                class="mt-6 track btn btn-primary justify-center w-full tablet:w-auto flex items-center text-sm widescreen:text-base"
                data-category="resi: checkout" data-action="resi: checkout summary - submit keycloak form"
                data-label="resi: checkout summary page" @click.prevent="submit()">
                <IconCircleNotch class="animate-spin text-white mr-2 text-lg" v-if="isLoading" />
                Create
            </button>
        </form>
        <AppModal v-if="modalVisibility" @dismiss="dismissModal()" :skipTrigger="true" :override-visibility="true"
            :has-borders="false" modal-type="info">
            <template #contents>
                <div class="p-6 flex flex-col">
                    <h3 class="text-3lg font-bold mb-2">
                        Success!
                    </h3>
                    <div class="text-sm mt-3 font-normal">
                        {{ modalCopy }}
                    </div>
                    <div class="text-sm mt-3 font-normal">
                        {{ closeWindowCopy }}
                    </div>
                    <div class="flex gap-2 py-2 justify-end">
                        <button class="buyflow-button-alt px-2" type="button" @click="dismissModal()">
                            Close
                        </button>
                        <a class="buyflow-button px-2" :href="modalLink" target="_blank" @click="dismissModal()">
                            {{ modalButton }}
                        </a>
                    </div>
                </div>
            </template>
        </AppModal>
    </div>
</template>

<script>
import axios from 'axios'
import XButton from '@/atoms/XButton'
import * as jose from 'jose'
import { captureException } from '@sentry/browser'
import {
    helpers,
    maxLength,
    minLength,
    required,
    sameAs,
    email,
} from 'vuelidate/lib/validators'
import IconPrint from '@/atoms/icons/IconPrint'
import IconEye from '@/atoms/icons/IconEye'
import IconEyeCancel from '@/atoms/icons/IconEyeCancel'
import IconCircleCheck from '../../../../components/atoms/icons/IconCircleCheck.vue'
import IconCircleExclamation from '../../../../components/atoms/icons/IconCircleExclamation.vue'
import IconCircleNotch from '../../../../components/atoms/icons/IconCircleNotch.vue'
import complexPassword from '../../../../validators/complexPassword'
import validUsername from '../../../../validators/validUsername'
import AppModal from '../AppModal'

// const usernameValidator = helpers.regex(/^[a-zA-Z0-9_][a-zA-Z0-9._-]+[a-zA-Z0-9_]$/)
// const validations = {
//     name: {
//         required: helpers.withMessage('Username can contain letters, numbers, underscores, dashes, and periods. Cannot start or end with a dash or period.', usernameValidator)
//     }
// }

export default {
    name: 'CreateAccount',
    components: {
        IconEye,
        AppModal,
        IconPrint,
        IconEyeCancel,
        IconCircleCheck,
        IconCircleNotch,
        IconCircleExclamation,
        XButton,
    },
    props: {
        uuid: {
            type: String,
            required: true,
        },
        firstName: {
            type: String,
            required: true,
        },
        lastName: {
            type: String,
            required: true,
        },
        accountId: {
            type: String,
            required: true,
        },
        locationId: {
            type: String,
            required: true,
        },
        accountCreated: {
            type: Boolean,
            required: true,
        },
        isMobileEligible: {
            type: Boolean,
            required: true,
        },
        isMobileInterested: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            passwordVisible: '',
            confirmPasswordVisible: '',
            accountCreation: true,
            modalVisibility: false,
            formFields: {
                username: '',
                password: '',
                confirmPassword: '',
                bzlEmail: false,
            },
            errors: {
                username: null,
                password: null,
            },
            jwe: {
                publicKey: '',
                alg: 'ECDH-ES',
                enc: 'A256GCM',
                encode: TextEncoder.prototype.encode.bind(new TextEncoder()),
            },
        }
    },
    validations: {
        formFields: {
            username: {
                required,
                maxLength: maxLength(30),
                validUsername
            },
            password: {
                required,
                minLength: minLength(8),
                maxLength: maxLength(24),
                complexPassword,
            },
            confirmPassword: {
                required,
                sameAsPassword: sameAs('password'),
            },
        },
    },
    mounted() {
        if (this.accountCreated) {
            this.accountCreation = false
        }
        this.$store
            .dispatch('Buyflow/_fetchKeycloakPublicKey')
            .then((publicKey) => {
                this.jwe.publicKey = publicKey
            })
    },
    watch: {
        'formFields.username'(val) {
            this.formFields.email = val + '@breezeline.net'
        },
    },
    computed: {
        usernameErrorMsg() {
            if (!this.$v.formFields.username.$error) return null;
            
            if (!this.$v.formFields.username.required) {
                return 'Username is required.';
            }
            if (!this.$v.formFields.username.maxLength) {
                return 'Username must be 30 characters or less.';
            }
            if (!this.$v.formFields.username.validUsername) {
                return 'Username may only contain letters and numbers. Do not use spaces.';
            }
            return 'Please enter a valid username.';
        },
        heading() {
            if (!this.accountCreation) {
                return 'Congratulations! Your Breezeline username and password have been accepted.'
            }

            return this.isMobileEligible && this.isMobileInterested
                ? 'You’re not done yet! Create your Breezeline Login to sign up for your Breezeline Mobile service.'
                : 'You’re almost done! Create your Breezeline Login to activate AutoPay & EcoSave to get up to a $10 monthly discount.'
        },
        modalCopy() {
            return this.isMobileEligible && this.isMobileInterested
                ? 'Your breezeline.net username and password have been created! Use these login credentials to access and manage your Breezeline account and mobile apps.'
                : 'Please proceed to My Account to activate AutoPay & EcoSave to get up to a $10 monthly discount.'
        },
        closeWindowCopy() {
            return this.isMobileEligible && this.isMobileInterested
                ? 'You may now close this window or proceed to Breezeline Mobile.'
                : null
        },
        modalButton() {
            return this.isMobileEligible && this.isMobileInterested
                ? 'Proceed to Breezeline Mobile'
                : 'Proceed to My Account'
        },
        modalLink() {
            return this.isMobileEligible && this.isMobileInterested
                ? process.env.MIX_REACH_URL ??
                'https://mobile.breezeline.com?new-account=true'
                : 'https://myaccount.breezeline.com'
        },
        meter() {
            const p = this.formFields.password
            const len = p?.length || 0
            let i = 0

            // Minimum of 1 Uppercase Letter
            i += /[A-Z]/.test(p) ? 1 : 0

            // Minimum of 1 lowercase Letter
            i += /[a-z]/.test(p) ? 1 : 0

            // Minimum of 1 Number
            i += /\d/.test(p) ? 1 : 0

            // Minimum of 1 Special character
            i += /[@!$%+/=~]/.test(p) ? 1 : 0

            // Between 8 and 24 characters
            i += len >= 8 && len <= 24 ? 1 : 0

            const color = [
                'bg-salmon',
                'bg-salmon',
                'bg-yellow-400',
                'bg-yellow-400',
                'bg-blue-light',
            ][i - 1]

            return [
                i > 0 ? color : '',
                i > 1 ? color : '',
                i > 2 ? color : '',
                i > 4 ? color : '',
                i > 4
                    ? 'Strong'
                    : len > 24
                        ? 'Too Long'
                        : len > 0
                            ? 'Too Weak'
                            : '',
            ]
        },
        passwordsMatch() {
            if (
                this.formFields.password?.length >= 8 &&
                this.formFields.password?.length <= 24
            ) {
                return (
                    this.formFields.password === this.formFields.confirmPassword
                )
            }
        },
    },
    methods: {
        dismissModal(error) {
            console.log("DismissModal Click", error)
            this.modalVisibility = false
            this.sendEvent(
                'resi:checkout',
                null,
                null,
                this.isMobileEligible && this.isMobileInterested
                    ? 'checkout_completion_proceed_to_mobile'
                    : 'checkout_completion_proceed_to_my_account'
            )
        },
        dismiss() {
            this.$emit('dismiss')
        },
        submit() {
            this.$v.$reset()
            this.errors = {
                username: null,
                password: null,
            }

            this.$v.$touch()

            if (!this.$v.$invalid) {
                const payload = {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    username: this.formFields.username,
                    password: this.formFields.password,
                    accountId: this.accountId,
                    locationId: this.locationId,
                    bzlEmail: this.formFields.bzlEmail,
                };
                this.isLoading = true
                this.encrypt(payload)
                    .then((jwe) => {
                        axios
                            .post('api/create-account', {
                                uuid: this.uuid,
                                jwe,
                                accountId: this.accountId
                            })
                            .then((response) => {
                                this.modalVisibility = true
                                this.accountCreation = false
                                this.sendEvent(
                                    'resi:checkout',
                                    null,
                                    null,
                                    'checkout_account_creation_success'
                                )
                            })
                            .catch((err) => {
                                captureException(err)

                                this.errors.username =
                                    {
                                        'AC-E01':
                                            'Account cannot be created at this time. Please try again later.',
                                        'AC-E02':
                                            'Sorry, a primary email login already exists for this account. If you forgot your email address, please click <a class="underline" target="_blank" href="https://create.my.breezeline.com/Retrieve">here</a>. To create an additional login for your account, please contact customer support: 1 (888) 536-9600.',
                                        'AC-E03':
                                            'Your order is still being processed. Please try again.',
                                        'AC-E04':
                                            'Account failed to be created, please click <a class="underline" target="_blank" href="https://create.my.breezeline.com/Create">here</a> to create an account, or call 1 (888) 536-9600.',
                                        'AC-E05':
                                            'Please choose a different username.',
                                        'AC-E06':
                                            'Account cannot be created at this time. Please contact customer support: 1 (888) 536-9600.',
                                        'AC-E07':
                                            'Account cannot be created at this time. Please contact customer support: 1 (888) 536-9600.',
                                        'AC-E08':
                                            'Account cannot be created at this time. Please contact customer support: 1 (888) 536-9600.',

                                    }[
                                    err.response.data.errors.substring(0, 6)
                                    ] ?? null

                                if (!this.errors.username) {
                                    this.errors.username =
                                        'Username taken or invalid, please enter a new username'
                                    this.errors.password =
                                        'Password must meet requirements above'
                                }
                                if (this.errors.username) {
                                    this.sendEvent(
                                        'resi:checkout',
                                        null,
                                        null,
                                        'checkout_account_creation_username_fail'
                                    )
                                }
                                if (this.errors.password) {
                                    this.sendEvent(
                                        'resi:checkout',
                                        null,
                                        null,
                                        'checkout_account_creation_password_fail'
                                    )
                                }
                                this.isLoading = false
                                console.log(err)
                            })
                            .finally(() => {
                                this.isLoading = false
                            })
                    })
                    .catch((err) => {
                        captureException(err)

                        this.sendEvent(
                            'resi:checkout',
                            null,
                            null,
                            'checkout_account_creation_jwe_encryption_fail'
                        )
                        this.errors.jwe = true
                        this.isLoading = false
                        console.log(err)
                    })
            }
        },
        async encrypt(payload) {
            this.isLoading = true
            try {
                const publicKey = await jose.importSPKI(
                    this.jwe.publicKey,
                    this.jwe.alg
                )
                //Encrypt token
                return await new jose.CompactEncrypt(
                    this.jwe.encode(JSON.stringify(payload))
                )
                    .setProtectedHeader({
                        alg: this.jwe.alg,
                        enc: this.jwe.enc,
                    })
                    .encrypt(publicKey)
            } catch (err) {
                captureException(err)
                throw err
            }
        },
    },
}
</script>

<style lang="scss">
.hide-pw-managers div,
.hide-pw-managers keeper-lock {
    display: none;
}
</style>
